import React, { useState, useRef } from "react";
import { Mail, Phone, User, MessageSquare, Check } from "lucide-react";
import SocialIcons from "./SocialIcons";
import { socialMedia } from "./socialMedia";
import { FormField } from "./FormField";
import { GlowingButton } from "./GlowingButton";

const ContactForm = () => {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    title: "",
    description: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    ContactNumber: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://backend.bestownrealty.com/api/SobahRouteThree/addContactForm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setIsSuccess(true);
        setToast({
          show: true,
          title: "Request Received",
          description:
            "Our team will contact you shortly regarding your inquiry.",
        });
        formRef.current?.reset();
        setFormData({
          name: "",
          email: "",
          ContactNumber: "",
          subject: "",
          message: "",
        });
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      setToast({
        show: true,
        title: "Error",
        description:
          "There was an error submitting your request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
    }
  };

  const features = [
    {
      title: "Personalized Service",
      description: "Tailored to your needs",
    },
    {
      title: "Expert Advice",
      description: "From our specialists",
    },
    {
      title: "Fast Response",
      description: "Within 24 hours",
    },
    {
      title: "No Obligation",
      description: "Just helpful information",
    },
  ];

  const contactDetails = [
    {
      icon: <Phone className="h-5 w-5 text-amber-200" />,
      title: "Phone",
      value: "+971 45 77 4390",
    },
    {
      icon: <Mail className="h-5 w-5 text-amber-200" />,
      title: "Email",
      value: "info@bestownrealty.ae",
    },
  ];

  return (
    <section
      className="w-full py-12 md:py-20 lg:py-24 relative overflow-hidden"
      id="contact"
    >
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-black opacity-90"></div>
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/956999/milky-way-starry-sky-night-sky-star-956999.jpeg')] bg-cover bg-center opacity-30 mix-blend-overlay"></div>

        {/* Animated decorative elements */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-400/10 rounded-full filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-300/10 rounded-full filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-3/4 left-1/2 w-96 h-96 bg-amber-200/10 rounded-full filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

        {/* Gold particle effect */}
        <div className="absolute inset-0 opacity-30">
          <div className="gold-particles"></div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Contact Information */}
          <div className="space-y-8 transform transition-all duration-700 hover:translate-y-[-5px]">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight relative inline-block">
                <span className="text-white relative z-10">Contact </span>
                <span className="gold-gradient-text relative z-10 text-amber-400/80">Us</span>
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-amber-400/80 to-transparent"></span>
              </h2>
              <p className="text-gray-300 text-lg max-w-md leading-relaxed animate-fade-in">
                Have questions or need more information? Reach out to our team
                and we'll get back to you as soon as possible.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              {features.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 group transition-all duration-300 hover:translate-x-1"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:gold-shadow">
                    <Check className="h-4 w-4 text-gray-900" />
                  </div>
                  <div>
                    <h4 className="text-amber-300 font-semibold group-hover:text-amber-200 transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact details */}
            <div className="space-y-6 pt-4">
              {contactDetails.map((detail, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 group transition-all duration-300 hover:translate-x-1"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-700 border border-amber-500/30 flex items-center justify-center transform transition-all duration-500 group-hover:border-amber-400/60 group-hover:scale-110">
                    {detail.icon}
                  </div>
                  <div>
                    <h4 className="text-gray-200 font-medium">
                      {detail.title}
                    </h4>
                    <p className="text-gray-300">{detail.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media */}
            <div className="pt-6">
              <h4 className="text-gray-200 font-medium mb-4 relative inline-block">
                Follow Us
                <span className="absolute -bottom-1 left-0 w-full h-px bg-gradient-to-r from-amber-400/50 to-transparent"></span>
              </h4>
              <div className="flex flex-wrap gap-3">
                {socialMedia.map((social, index) => (
                  <SocialIcons key={index} social={social} index={index} />
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Contact Form */}
          <div className="backdrop-blur-md bg-black/30 rounded-xl border border-amber-500/20 shadow-[0_0_15px_rgba(210,172,71,0.15)] overflow-hidden transition-all duration-500 hover:shadow-[0_0_25px_rgba(210,172,71,0.25)]">
            <div className="p-1">
              <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-lg p-6 md:p-8 relative">
                {/* Success overlay */}
                {isSuccess && (
                  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-50 animate-fade-in rounded-lg">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-4 animate-bounce">
                      <Check className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-medium text-white mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-gray-300 text-center">
                      Thank you for your message. We'll be in touch soon.
                    </p>
                  </div>
                )}

                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 to-amber-500 animate-gradient-shift">
                    Get In Touch
                  </span>
                  <span className="ml-2 h-px flex-1 bg-gradient-to-r from-amber-500/50 to-transparent"></span>
                </h3>

                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      id="name"
                      name="name"
                      type="text"
                      label="Full Name"
                      placeholder="Your name"
                      icon={<User className="h-5 w-5 text-gray-500" />}
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />

                    <FormField
                      id="email"
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="xyz@email.com"
                      icon={<Mail className="h-5 w-5 text-gray-500" />}
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      id="ContactNumber"
                      name="ContactNumber"
                      type="tel"
                      label="Contact Number"
                      placeholder="+1 234 567 8901"
                      icon={<Phone className="h-5 w-5 text-gray-500" />}
                      value={formData.ContactNumber}
                      onChange={handleChange}
                      required
                    />

                    <FormField
                      id="subject"
                      name="subject"
                      type="text"
                      label="Subject"
                      placeholder="How can we help?"
                      icon={<MessageSquare className="h-5 w-5 text-gray-500" />}
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="message"
                      className="text-sm font-medium text-gray-300 block"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Please provide details about your inquiry..."
                      onChange={handleChange}
                      value={formData.message}
                      required
                      className="w-full px-3 py-2 rounded-md
                      bg-gray-800/50 border border-gray-700 
                      text-gray-200 placeholder:text-gray-500
                      focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 
                      focus:outline-none transition-all duration-300 form-input-effect
                      resize-none"
                    ></textarea>
                  </div>

                  <GlowingButton
                    type="submit"
                    disabled={isSubmitting}
                    isSubmitting={isSubmitting}
                  />

                 
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
